import { doc, getDoc } from 'firebase/firestore';
import type {
  ProposalBuilderVersionDoc,
  ProposalNodeSet,
  ProposalVersionSource,
  RfqHubFileRelease,
  RfqPackage,
} from '../types';
import { db } from './firebase';
import { releaseVersionLabel } from './timberRfqPrePortalVersion';
import { formatProposalVersionTimestamp } from './proposalVersioning';

export type ProposalRfqLinkFields = {
  linkedProposalVersionId?: string | null;
  linkedProposalVersionLabel?: string;
  linkedProposalVersionSource?: ProposalVersionSource;
  linkedProposalVersionSavedAt?: Date;
  linkedFrom?: 'proposal_push' | 'hub_file_stage';
  releaseVersionIndex?: number;
};

export function proposalRfqLinkFromRelease(release: RfqHubFileRelease): ProposalRfqLinkFields {
  return {
    linkedProposalVersionId: release.linkedProposalVersionId,
    linkedProposalVersionLabel: release.linkedProposalVersionLabel,
    linkedProposalVersionSource: release.linkedProposalVersionSource,
    linkedProposalVersionSavedAt: release.linkedProposalVersionSavedAt,
    linkedFrom: release.linkedFrom,
    releaseVersionIndex: release.releaseVersionIndex,
  };
}

export function proposalRfqLinkFromPackage(pkg: RfqPackage): ProposalRfqLinkFields {
  return {
    linkedProposalVersionId: pkg.linkedProposalVersionId ?? pkg.proposalVersionId,
    linkedProposalVersionLabel: pkg.linkedProposalVersionLabel,
    linkedProposalVersionSource: pkg.linkedProposalVersionSource,
    linkedProposalVersionSavedAt: pkg.linkedProposalVersionSavedAt,
    linkedFrom: pkg.linkedFrom,
    releaseVersionIndex: pkg.releaseVersionIndex,
  };
}

export function formatProposalVersionLabelForRfq(link: ProposalRfqLinkFields): string | null {
  const label = (link.linkedProposalVersionLabel || '').trim();
  if (label) return label;
  if (link.linkedProposalVersionId) return 'Saved proposal version';
  return null;
}

export function formatProposalRfqLinkText(link: ProposalRfqLinkFields): string | null {
  const rfqPart =
    typeof link.releaseVersionIndex === 'number'
      ? releaseVersionLabel(link.releaseVersionIndex)
      : '';
  const proposalPart = formatProposalVersionLabelForRfq(link);
  if (!rfqPart && !proposalPart) return null;
  if (rfqPart && proposalPart) return `${rfqPart} ↔ ${proposalPart}`;
  return rfqPart || proposalPart;
}

export function formatSupplierDesignVersionSubtitle(link: ProposalRfqLinkFields): string | null {
  const label = formatProposalVersionLabelForRfq(link);
  if (!label) return null;
  const savedAt = link.linkedProposalVersionSavedAt;
  if (savedAt instanceof Date && !Number.isNaN(savedAt.getTime())) {
    const when = savedAt.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `Design version: ${label} (${when})`;
  }
  return `Design version: ${label}`;
}

export function formatProposalVersionForRfq(version: ProposalBuilderVersionDoc): string {
  const label = (version.label || '').trim();
  if (label) return label;
  return formatProposalVersionTimestamp(version);
}

export async function loadLinkedProposalNodeSets(
  dealId: string,
  link: ProposalRfqLinkFields,
): Promise<ProposalNodeSet[] | null> {
  const versionId = (link.linkedProposalVersionId || '').trim();
  if (!versionId) return null;
  const snap = await getDoc(doc(db, 'proposalBuilders', dealId, 'versions', versionId));
  if (!snap.exists()) return null;
  const data = snap.data() as { snapshot?: { nodeSets?: ProposalNodeSet[] } };
  return data.snapshot?.nodeSets ?? null;
}

export function inferProposalLinkFromAppliedFile(
  appliedFileId: string | null | undefined,
  sourceFileId: string,
  versions: ProposalBuilderVersionDoc[],
): ProposalBuilderVersionDoc | null {
  const applied = (appliedFileId || '').trim();
  if (!applied || applied !== sourceFileId.trim()) return null;
  for (const v of versions) {
    const snapApplied = v.snapshot?.timberListAppliedToProposalFileId?.trim();
    if (snapApplied === applied) return v;
  }
  const priority: ProposalVersionSource[] = ['rfq_staging', 'ops_approved', 'sent', 'manual'];
  for (const source of priority) {
    const match = versions.find(v => v.source === source);
    if (match) return match;
  }
  return versions[0] ?? null;
}

export function formatProposalLinkBadge(link: ProposalRfqLinkFields): string | null {
  const label = formatProposalVersionLabelForRfq(link);
  if (label) return `Proposal: ${label}`;
  return null;
}

export function formatProposalVersionSupplierRfqCell(version: ProposalBuilderVersionDoc): string {
  const label = (version.label || '').trim();
  if (version.source === 'rfq_staging' && label) return label;
  const linkedCount = version.linkedRfqReleaseIds?.length ?? 0;
  if (linkedCount === 1) return 'Linked RFQ release';
  if (linkedCount > 1) return `${linkedCount} RFQ releases`;
  return '—';
}

export function publishLinkFieldsFromHubRelease(release: RfqHubFileRelease): ProposalRfqLinkFields & {
  proposalVersionId: string | null;
  timberListSource: 'proposal_saved_version' | 'hub_staged_release';
} {
  const linkedId = release.linkedProposalVersionId?.trim() || null;
  const link = proposalRfqLinkFromRelease(release);
  return {
    ...link,
    proposalVersionId: linkedId,
    timberListSource: linkedId ? 'proposal_saved_version' : 'hub_staged_release',
  };
}
